import {useForm} from "react-hook-form";
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter} from "@/components/ui/dialog";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {Checkbox} from "@/components/ui/checkbox";
import {Form, FormField, FormItem, FormLabel, FormControl, FormMessage} from "@/components/ui/form";
import {
    HistoryEntryEditFormPayload,
    HistoryEntryFormPayload,
    HistoryEntryUpdateSchema
} from "@/lib/types/forms.ts";
import {usePage} from "@inertiajs/react";
import {zodResolver} from "@hookform/resolvers/zod";
import {HistoryEntry} from "@/lib/types/models.ts";
import {useEffect} from "react";
import DatePicker from "@/components/ui/DatePicker.tsx";

interface EditHistoryEntryDialogProps {
    isOpen: boolean,
    onOpenChange: (open: boolean) => void,
    onUpdate: (data: HistoryEntryEditFormPayload) => Promise<void>,
    entry: HistoryEntry
}

export default function EditHistoryEntryDialog({
                                                   isOpen,
                                                   onOpenChange,
                                                   onUpdate,
                                                   entry
                                               }: EditHistoryEntryDialogProps) {
    const {translations} = usePage().props as unknown as { translations: any };
    const t = translations.edit_history_dialog;

    const form = useForm<HistoryEntryFormPayload>({
        resolver: zodResolver(HistoryEntryUpdateSchema),
        defaultValues: {
            appointment_at: entry.appointment_at ?? "",
            patient_card_id: entry.patient_card_id,
            bill: entry.bill ?? undefined,
            paid: entry.paid ?? false,
            tooth: entry.tooth ?? "",
            work_done: entry.work_done ?? "",
            event_id: entry.event_id ?? undefined,
        },
    });

    useEffect(() => {
        form.reset({
            appointment_at: entry.appointment_at ?? "",
            patient_card_id: entry.patient_card_id,
            bill: entry.bill != null ? Number(entry.bill) : undefined,
            paid: entry.paid ?? false,
            tooth: entry.tooth ?? "",
            work_done: entry.work_done ?? "",
            event_id: entry.event_id ?? undefined,
        });
    }, [entry]);

    const handleSubmit = async (data: HistoryEntryEditFormPayload) => {
        await onUpdate(data);
    };

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90vh] overflow-auto">
                <DialogHeader>
                    <DialogTitle>{t.title}</DialogTitle>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                        <FormField
                            control={form.control}
                            name="appointment_at"
                            render={({field}) => (
                                <FormItem>
                                    <FormLabel>{t.fields.appointment_at}</FormLabel>
                                    <FormControl>
                                        <DatePicker
                                            value={field.value}
                                            onChange={field.onChange}
                                        />
                                    </FormControl>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="work_done"
                            render={({field}) => (
                                <FormItem>
                                    <FormLabel>{t.fields.work_done}</FormLabel>
                                    <FormControl>
                                        <Input {...field} value={field.value ?? ""}/>
                                    </FormControl>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="tooth"
                            render={({field}) => (
                                <FormItem>
                                    <FormLabel>{t.fields.tooth}</FormLabel>
                                    <FormControl>
                                        <Input {...field} value={field.value ?? ""}/>
                                    </FormControl>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="bill"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t.fields.bill}</FormLabel>
                                    <FormControl>
                                        <Input
                                            type="number"
                                            min={0}
                                            {...field}
                                            value={field.value ?? 0}
                                            onChange={(e) =>
                                                field.onChange(e.target.value === "" ? undefined : Number(e.target.value))
                                            }
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="paid"
                            render={({field}) => (
                                <FormItem className="flex flex-row items-center space-x-3">
                                    <FormLabel>{t.fields.paid}</FormLabel>
                                    <FormControl>
                                        <Checkbox
                                            checked={field.value ?? false}
                                            onCheckedChange={(checked) => field.onChange(checked)}
                                        />
                                    </FormControl>
                                </FormItem>
                            )}
                        />

                        <DialogFooter className="flex justify-end gap-2">
                            <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>
                                {t.buttons.cancel}
                            </Button>
                            <Button type="submit">
                                {t.buttons.save}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}